import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { StatusBar } from './StatusBar';
import { LogOut, UserCircle, BellRing, RefreshCw } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

export function GlobalShell() {
  const { user, signOut } = useAuth();
  const { pendingSyncCount } = useOfflineSync();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Service Worker robust update detection via vite-plugin-pwa
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      // Periodically check for updates every hour
      r && setInterval(() => {
        r.update();
      }, 60 * 60 * 1000);
    },
    onRegisterError(error) {
      console.error('SW registration error', error);
    }
  });

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      {/* ── Service Worker Alert ── */}
      {needRefresh && (
        <div className="bg-indigo-600 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BellRing size={16} />
            <span className="text-sm font-medium font-secondary">New Update Available: Click to Refresh</span>
          </div>
          <button
            onClick={() => updateServiceWorker(true)}
            className="text-sm font-bold underline"
          >
            <span className="font-secondary">Refresh</span>
          </button>
        </div>
      )}

      {/* ── Top Navigation Bar ── */}
      <header className="bg-white border-b border-divider px-4 py-3 flex items-center justify-between z-[60] relative">
        <Link to={user?.role === 'admin' ? '/admin/dashboard' : '/teacher/dashboard'} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center overflow-hidden">
            <img
              src="/logo-circle.png"
              alt="Shiksha-Edge"
              className="w-7 h-7 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span class="text-indigo-600 font-bold text-lg leading-none">SE</span>';
              }}
            />
          </div>
          <span className="text-xl font-bold tracking-tight text-ink font-secondary">Shiksha-Edge</span>
        </Link>

        <div className="flex items-center gap-3">
          {/* ── Global Sync Queue Counter ── */}
          {pendingSyncCount > 0 && (
            <button
              onClick={() => navigate('/sync-hub')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold hover:bg-amber-100 transition-colors"
            >
              <RefreshCw size={12} />
              <span className="font-secondary">{pendingSyncCount} pending</span>
            </button>
          )}

          {/* ── User Avatar / Dropdown ── */}
          {user && (
            <div className="flex items-center gap-2">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={(e) => { e.stopPropagation(); setDropdownOpen(!dropdownOpen); }}
                  className="flex items-center gap-2 hover:bg-gray-50 px-2 py-1.5 rounded-lg transition-colors cursor-pointer"
                >
                  <UserCircle size={24} className="text-graphite" />
                  <span className="text-sm font-medium text-ink hidden sm:block font-secondary">
                    {user.displayName || user.email}
                  </span>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-divider rounded-xl shadow-2xl py-1 z-[9999] top-full">
                    <div className="px-4 py-2 border-b border-gray-100 mb-1">
                      <p className="text-sm font-bold text-ink truncate">{user.displayName || 'Teacher'}</p>
                      <p className="text-xs text-graphite truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        alert('Profile settings coming soon!');
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-ink hover:bg-gray-50 transition-colors font-secondary text-left"
                    >
                      <UserCircle size={16} />
                      Edit Profile
                    </button>
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        signOut();
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-conflict hover:bg-red-50 transition-colors font-secondary text-left font-bold"
                    >
                      <LogOut size={16} />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => signOut()}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-conflict hover:bg-red-50 transition-colors text-sm font-bold"
                title="Log Out"
              >
                <LogOut size={16} />
                <span className="font-secondary">Log Out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Network & Sync Status ── */}
      <StatusBar />

      {/* ── Page Content ── */}
      <main className="flex-1 relative">
        <Outlet />
      </main>
    </div>
  );
}
